import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Package, MapPin, Lock, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { toast } from 'sonner';
import { AccountSidebar } from '../components/account/AccountSidebar';
import { ProfileTab } from '../components/account/ProfileTab';
import { OrdersTab } from '../components/account/OrdersTab';
import { AddressTab } from '../components/account/AddressTab';
import { PasswordTab } from '../components/account/PasswordTab';

const SHOP_OWNER_EMAIL = 'daizyinfused@gmail.com';

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'orders', label: 'Order History', icon: Package },
  { id: 'address', label: 'Shipping Address', icon: MapPin },
  { id: 'password', label: 'Change Password', icon: Lock },
];

const Account = () => {
  const { user, loading, isAuthenticated, logout, updateProfile, changePassword, getOrders } = useAuth();
  const { wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [saving, setSaving] = useState(false);

  const isShopOwner = user?.email?.toLowerCase() === SHOP_OWNER_EMAIL.toLowerCase();

  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        phone: user.phone || '',
        line1: user.shipping_address?.line1 || '',
        line2: user.shipping_address?.line2 || '',
        city: user.shipping_address?.city || '',
        state: user.shipping_address?.state || '',
        postal_code: user.shipping_address?.postal_code || '',
        country: user.shipping_address?.country || 'US',
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoadingOrders(true);
      try {
        const orderList = await getOrders();
        setOrders(orderList);
      } catch (error) {
        toast.error('Failed to load orders');
      } finally {
        setLoadingOrders(false);
      }
    };
    if (activeTab === 'orders' && isAuthenticated) {
      fetchOrders();
    }
  }, [activeTab, isAuthenticated, getOrders]);

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updateData = {
        name: profileData.name,
        phone: profileData.phone || null,
      };

      if (profileData.line1) {
        updateData.shipping_address = {
          line1: profileData.line1,
          line2: profileData.line2 || null,
          city: profileData.city,
          state: profileData.state,
          postal_code: profileData.postal_code,
          country: profileData.country,
        };
      }

      await updateProfile(updateData);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 to-purple-900 flex items-center justify-center">
        <Sparkles className="w-12 h-12 text-yellow-400 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h1 className="text-4xl font-bold text-white mb-2">My Account</h1>
          <p className="text-purple-200">Welcome back, {user?.name}!</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          <div className="lg:col-span-1">
            <AccountSidebar
              tabs={TABS}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              wishlistCount={wishlistCount}
              isShopOwner={isShopOwner}
              onLogout={handleLogout}
            />
          </div>

          <div className="lg:col-span-3">
            {activeTab === 'profile' && (
              <ProfileTab
                profileData={profileData}
                userEmail={user?.email}
                saving={saving}
                onChange={handleProfileChange}
                onSubmit={handleProfileSubmit}
              />
            )}
            {activeTab === 'orders' && (
              <OrdersTab orders={orders} loadingOrders={loadingOrders} />
            )}
            {activeTab === 'address' && (
              <AddressTab
                profileData={profileData}
                saving={saving}
                onChange={handleProfileChange}
                onSubmit={handleProfileSubmit}
              />
            )}
            {activeTab === 'password' && (
              <PasswordTab
                passwordData={passwordData}
                saving={saving}
                onChange={handlePasswordChange}
                onSubmit={handlePasswordSubmit}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
